import type {
  NumberInputFieldProps,
  NumberInputProps
} from '@chakra-ui/react'
import {
  InputGroup,
  InputRightElement,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper
} from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'

export interface CustomNumberInputProps extends Omit<NumberInputProps, 'defaultValue'> {
  onValueChange?: (value: string, valueAsNumber?: number) => void
  defaultValue?: number | undefined | null
  inputRightElement?: string | React.ReactNode | null
  inputProps?: NumberInputFieldProps
  decimalPlaces?: number
  showStepper?: boolean
}

export const CustomNumberInput = React.forwardRef(({
  onValueChange,
  defaultValue,
  inputRightElement = 'Kč',
  inputProps,
  decimalPlaces = 0,
  showStepper = false,
  ...rest
}: CustomNumberInputProps, ref
) => {
  const [value, setValue] = useState(defaultValue)

  const format = (val: number | undefined) => {
    if (val === undefined) return ''
    return new Intl.NumberFormat('cs-CZ', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    }).format(Number(val))
  }

  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  return (
    <NumberInput as={InputGroup}
      ref={ref}
      value={format(value ?? undefined)}
      focusInputOnChange={false}
      onChange={(_: string, valueAsNumber: number) => {
        const val = isNaN(valueAsNumber) ? undefined : valueAsNumber
        setValue(val)
        onValueChange?.(val !== undefined ? String(val) : '', val)
      }}
      precision={decimalPlaces}
      step={decimalPlaces > 0 ? Math.pow(10, -decimalPlaces) : 1}
      {...rest}
    >
      <NumberInputField maxW={128} minW='100%' {...inputProps} />

      {inputRightElement && <InputRightElement>{inputRightElement}</InputRightElement>}

      {showStepper && (
        <NumberInputStepper>
          <NumberIncrementStepper />
          <NumberDecrementStepper />
        </NumberInputStepper>
      )}
    </NumberInput>
  )
}
)